import { useState } from 'react'
import { useNavigate, Link } from './nav'
import { CheckCircle, XCircle, MinusCircle, Clock, ExternalLink, Bug, Pause, ArrowUpRight, ChevronRight, ChevronDown } from 'lucide-react'
import Spinner from './Spinner'
import type { Journey } from '../data/projectTypes'
import { isBugResolvedForDisplay, isSurfacedFinding } from '../../netlify/functions/lib/bug-management/statuses'
import { pauseTooltip, pausedStatus, type ProjectPause } from '../lib/projectPause'
import { taskDetailUrl } from '../lib/taskDetailPath'
import { useAdminMode } from '../hooks/useAdminMode'
import { BUG_FOUND_RUN_STATUS, OUTCOME_STATUS_LABELS, isAttentionRunStatus } from '../guidance/journey/run-status'
import './JourneysTab.css'

export function JourneySourceLink({ journey, projectId }: { journey: Journey; projectId: string }) {
  // The "Created by" attribution links into the admin-only exploration task detail page.
  const adminMode = useAdminMode()
  const latestVersion = journey.versions?.[0]
  const explorationId = latestVersion?.exploration_id
  if (explorationId) {
    return (
      <div className="journeys-tab__source-link">
        <ArrowUpRight size={14} aria-hidden />
        <span>Created by </span>
        {adminMode ? (
          <Link to={taskDetailUrl(projectId, explorationId)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} data-analytics-event="journey-source-exploration-link">
            Exploration
          </Link>
        ) : (
          <span>Exploration</span>
        )}
      </div>
    )
  }
  return null
}

// `pause`, when given, is the project's pause: a journey or journey version whose last run was frozen
// mid-flight still reports 'in-progress' forever, and reads as a run that is still going. The icon-only
// surfaces carry the reason as a native title tooltip (the labelled badge is StatusIndicator).
export function JourneyStatusIcon({ status: storedStatus, pause = null }: { status: string | null; pause?: ProjectPause | null }) {
  const status = pausedStatus(storedStatus, pause)
  if (!status) return null
  if (status === 'paused') {
    return (
      <span className="journeys-tab__status-icon-pause" title={pauseTooltip(pause)}>
        <Pause size={14} className="journeys-tab__status-icon journeys-tab__status-icon--paused" role="img" aria-label="Paused" />
      </span>
    )
  }
  if (status === 'completed') return <CheckCircle size={14} className="journeys-tab__status-icon journeys-tab__status-icon--completed" role="img" aria-label="Completed" />
  if (status === 'failed') return <XCircle size={14} className="journeys-tab__status-icon journeys-tab__status-icon--failed" role="img" aria-label="Failed" />
  if (status === BUG_FOUND_RUN_STATUS) return <Bug size={14} className="journeys-tab__status-icon journeys-tab__status-icon--bug-found" role="img" aria-label={OUTCOME_STATUS_LABELS.bug} />
  if (status === 'infra-failed') return <XCircle size={14} className="journeys-tab__status-icon journeys-tab__status-icon--failed" role="img" aria-label="Infra failed" />
  if (status === 'incomplete') return <MinusCircle size={14} className="journeys-tab__status-icon journeys-tab__status-icon--incomplete" role="img" aria-label="Incomplete" />
  // 'blocked' — the run stopped at an obstacle it cannot pass (docs/backend/test-runs.md `blocked`). Not an
  // X: no verdict on the app and no bug to look at. Same treatment a blocked exploration gets.
  if (status === 'blocked') return <MinusCircle size={14} className="journeys-tab__status-icon journeys-tab__status-icon--blocked" role="img" aria-label="Blocked" />
  // "Queued" = the run's container task has not been claimed yet; "In progress" = a worker is
  // actively running it. The clock-vs-spinner split makes that distinction visible.
  if (status === 'queued') return <Clock size={14} className="journeys-tab__status-icon journeys-tab__status-icon--queued" role="img" aria-label="Queued" />
  return <Spinner size={14} className="journeys-tab__status-icon journeys-tab__status-icon--in-progress" label="In progress" />
}

function parseFailedStep(error: string | null): { stepIndex: number; message: string } | null {
  if (!error) return null
  const checkMatch = error.match(/^Check failed at step (\d+): (.+)/)
  if (checkMatch) return { stepIndex: parseInt(checkMatch[1]!, 10) - 1, message: checkMatch[2]! }
  const errorMatch = error.match(/^Errors detected after step (\d+): (.+)/)
  if (errorMatch) return { stepIndex: parseInt(errorMatch[1]!, 10) - 1, message: errorMatch[2]! }
  return null
}

export function JourneyBugBanner({ journey, projectId }: { journey: Journey; projectId: string }) {
  const navigate = useNavigate()
  // The journey-level bug fields aren't always populated even when the latest run filed a bug —
  // the bug info lives on the most recent version. Fall back to it so the banner still surfaces.
  const latestVersion = journey.versions?.[0]
  const bugId = journey.last_run_bug_id ?? latestVersion?.last_run_bug_id
  const bugStatus = journey.last_run_bug_status ?? latestVersion?.last_run_bug_status
  const bugTitle = journey.last_run_bug_title ?? latestVersion?.last_run_bug_title
  if (!bugId || !isSurfacedFinding(bugStatus)) return null
  const resolved = isBugResolvedForDisplay(bugStatus)
  return (
    <div
      className={`journeys-tab__bug-banner${resolved ? ' journeys-tab__bug-banner--resolved' : ''}`}
      data-testid={`bug-banner-${journey.id}`}
    >
      <div className="journeys-tab__bug-banner-icon">
        {resolved ? <CheckCircle size={16} /> : <Bug size={16} />}
      </div>
      <div className="journeys-tab__bug-banner-content">
        <span className="journeys-tab__bug-banner-label">{resolved ? 'Bug resolved' : 'Bug found'}</span>
        {bugTitle && <span className="journeys-tab__bug-banner-title">{bugTitle}</span>}
      </div>
      <button
        type="button"
        className="journeys-tab__bug-banner-link"
        onClick={() => navigate(`/projects/${projectId}/bugs/${bugId}`)}
        data-testid={`bug-banner-link-${bugId}`}
        data-analytics-event="journey-bug-banner-link"
      >
        View bug <ExternalLink size={12} />
      </button>
    </div>
  )
}

// Displays a journey's steps as the OPAQUE `JourneySteps` aggregate defined by the src/guidance/journey/steps.ts
// guidance. The app does not know the representation, so it shows the aggregate structure verbatim
// (serialized) rather than interpreting it — whatever shape the guidance uses is what you see.
// `stepCount` (0 = unstepped) gates whether there's anything to show.
export function ActionsList({ steps, stepCount, lastRunError, lastRunStatus }: { steps?: unknown; stepCount?: number; lastRunError?: string | null; lastRunStatus?: string | null }) {
  // Steps blob is collapsed by default — it can be large; expand on demand.
  const [expanded, setExpanded] = useState(false)
  if (!stepCount || stepCount === 0) return null
  // Every terminal status that isn't a clean pass warrants surfacing the run's reason against the step it
  // stopped on — 'incomplete' and 'blocked' included, which previously showed nothing at all.
  const isFailed = isAttentionRunStatus(lastRunStatus)
  const failure = isFailed ? parseFailedStep(lastRunError ?? null) : null
  const failureMessage = failure?.message ?? (isFailed ? lastRunError : null)
  let serialized: string
  try { serialized = JSON.stringify(steps, null, 2) } catch { serialized = String(steps) }
  return (
    <div className="journeys-tab__actions">
      <button type="button" className="journeys-tab__section-title journeys-tab__steps-toggle" onClick={() => setExpanded(v => !v)} data-testid="journey-steps-toggle" data-analytics-event="journey-steps-toggle">
        {expanded ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />} Steps ({stepCount})
      </button>
      {failureMessage && (
        <div className="journeys-tab__run-error-banner" data-testid="run-error-banner">
          <XCircle size={14} aria-hidden />
          <span>{failureMessage}</span>
        </div>
      )}
      {expanded && <pre className="journeys-tab__steps-aggregate" data-testid="journey-steps-aggregate">{serialized}</pre>}
    </div>
  )
}
